/* eslint-disable react/jsx-no-bind */
import { Settings, Search, Clear, WarningAmber, Refresh } from '@mui/icons-material';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { S3_URL } from '../../constants/baseUrl';
import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';

import parcelWeightService from './parcelWeights';

const ParcelWeights = () => {
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: { package_weights: [] },
    status_code: 0,
  });
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newParcelWeight, setNewParcelWeight] = useState({
    packageWeightName: '',
    packageIcon: '',
    isHeavyVehicle: false,
    orderId: '',
    additionalCharge: '',
  });
  const [selectedParcelWeight, setSelectedParcelWeight] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({
    packageWeightName: '',
    packageIcon: '',
    isHeavyVehicle: '',
    orderId: '',
    additionalCharge: '',
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Extract and filter parcel weight data
  const parcelWeightData = apiResponse.data?.package_weights || [];
  const filteredData = parcelWeightData.filter(
    (item) =>
      search === '' ||
      item.packageWeightName.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    fetchParcelWeights();
  }, []);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('parcle_weight_icons/')) return `${S3_URL}/${url}`;
    return `${S3_URL}/parcle_weight_icons/${url}`;
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedParcelWeight(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const validateInputs = () => {
    let valid = true;
    const newErrors = {
      packageWeightName: '',
      packageIcon: '',
      isHeavyVehicle: '',
      orderId: '',
      additionalCharge: '',
    };

    // Weight Name: Required when adding, optional when editing (no minimum length)
    if (!isEditMode) {
      if (
        !newParcelWeight.packageWeightName ||
        !newParcelWeight.packageWeightName.trim()
      ) {
        newErrors.packageWeightName = 'Weight Name is required.';
        valid = false;
      }
    }

    // Weight Name: letters, numbers, spaces, apostrophe, hyphen
    const weightNameTrimmed = (newParcelWeight.packageWeightName || '').trim();
    if (weightNameTrimmed) {
      const weightNameRegex = /^[A-Za-z0-9\s'-]+$/;
      if (!weightNameRegex.test(weightNameTrimmed)) {
        newErrors.packageWeightName =
          'Weight name may only use letters, numbers, spaces, apostrophes, and hyphens.';
        valid = false;
      }
    }

    // Package Icon: Required when adding, optional when editing
    if (!isEditMode) {
      if (!newParcelWeight.packageIcon || !newParcelWeight.packageIcon.trim()) {
        newErrors.packageIcon = 'Package icon is required.';
        valid = false;
      }
    }

    // Package Icon: alphabetic only, maximum 5 characters (when provided)
    const packageIconTrimmed = (newParcelWeight.packageIcon || '').trim();
    if (packageIconTrimmed) {
      const packageIconRegex = /^[A-Za-z\s'-]+$/;
      if (!packageIconRegex.test(packageIconTrimmed)) {
        newErrors.packageIcon =
          'Package icon should contain only letters and spaces.';
        valid = false;
      } else if (packageIconTrimmed.length > 5) {
        newErrors.packageIcon = 'Package icon must be at most 5 characters.';
        valid = false;
      }
    }

    if (
      newParcelWeight.isHeavyVehicle === null ||
      newParcelWeight.isHeavyVehicle === undefined
    ) {
      newErrors.isHeavyVehicle = 'This field is required.';
      valid = false;
    }

    if (!newParcelWeight.orderId) {
      newErrors.orderId = 'Order ID is required.';
      valid = false;
    } else if (
      isNaN(newParcelWeight.orderId) ||
      parseInt(newParcelWeight.orderId) < 0
    ) {
      newErrors.orderId = 'Order ID must be a valid positive integer.';
      valid = false;
    }

    const additionalChargeRaw = newParcelWeight.additionalCharge;
    const additionalChargeStr =
      additionalChargeRaw === null || additionalChargeRaw === undefined
        ? ''
        : String(additionalChargeRaw).trim();

    const validateAdditionalChargeFormat = () => {
      if (
        isNaN(additionalChargeRaw) ||
        !Number.isInteger(Number(additionalChargeRaw)) ||
        parseInt(additionalChargeRaw, 10) < 0
      ) {
        newErrors.additionalCharge =
          'Additional charge must be a valid non-negative integer.';
        valid = false;
      }
    };

    if (!isEditMode) {
      if (additionalChargeStr === '') {
        newErrors.additionalCharge = 'Additional charge is required.';
        valid = false;
      } else {
        validateAdditionalChargeFormat();
      }
    } else if (additionalChargeStr !== '') {
      validateAdditionalChargeFormat();
    }

    setErrors(newErrors);
    return valid;
  };

  const handleModalOpen = (editMode = false) => {
    setIsEditMode(editMode);
    if (editMode && selectedParcelWeight) {
      setNewParcelWeight({
        packageWeightName: selectedParcelWeight.packageWeightName,
        packageIcon:
          selectedParcelWeight.packageWeightIcon ||
          selectedParcelWeight.packageIcon,
        isHeavyVehicle:
          selectedParcelWeight.isHeavyVehicle != null
            ? selectedParcelWeight.isHeavyVehicle
            : '',
        orderId:
          selectedParcelWeight.orderId ?? selectedParcelWeight.order ?? '',
        additionalCharge:
          selectedParcelWeight.additionalCharge != null
            ? selectedParcelWeight.additionalCharge
            : '',
      });
    } else {
      setNewParcelWeight({
        packageWeightName: '',
        packageIcon: '',
        isHeavyVehicle: false,
        orderId: '',
        additionalCharge: '',
      });
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewParcelWeight({
      packageWeightName: '',
      packageIcon: '',
      isHeavyVehicle: false,
      orderId: '',
      additionalCharge: '',
    });
    setErrors({
      packageWeightName: '',
      packageIcon: '',
      isHeavyVehicle: '',
      orderId: '',
      additionalCharge: '',
    });
    setSelectedParcelWeight(null);
  };

  const fetchParcelWeights = async () => {
    setLoading(true);
    try {
      const res = await parcelWeightService.fetchParcelWeights();
      setApiResponse(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error fetching parcel weights');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchParcelWeights();
    toast.success('Data refreshed successfully');
  };

  const handleAddParcelWeight = () => {
    if (validateInputs()) {
      setActionLoading(true);

      const params = new URLSearchParams();
      params.append('packageWeightName', newParcelWeight.packageWeightName);
      params.append('isHeavyVehicle', newParcelWeight.isHeavyVehicle);
      params.append('orderId', newParcelWeight.orderId);
      params.append('additionalCharge', newParcelWeight.additionalCharge);
      params.append('packageIcon', newParcelWeight.packageIcon);

      parcelWeightService
        .addParcelWeight(params)
        .then(() => {
          toast.success('Package weight created successfully!');
          fetchParcelWeights();
          handleModalClose();
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message;
          if (error.response?.data?.status === false) {
            toast.error(errorMessage);
          } else {
            toast.error(
              'Error adding parcel weight:',
              error.response?.data?.detail || error.message,
            );
          }
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleEditParcelWeight = () => {
    if (validateInputs()) {
      setActionLoading(true);
      const queryParams = {
        packageWeightId: selectedParcelWeight.packageWeightId,
        isHeavyVehicle: newParcelWeight.isHeavyVehicle,
      };

      // Only include packageWeightName if it has changed from the original
      const originalWeightName = (
        selectedParcelWeight.packageWeightName || ''
      ).trim();
      const newWeightName = (newParcelWeight.packageWeightName || '').trim();
      if (newWeightName && newWeightName !== originalWeightName) {
        queryParams.packageWeightName = newWeightName;
      }

      // Only include packageIcon if it has changed from the original
      const originalIcon = (
        selectedParcelWeight.packageWeightIcon ||
        selectedParcelWeight.packageIcon ||
        ''
      ).trim();
      const newIcon = (newParcelWeight.packageIcon || '').trim();
      if (newIcon && newIcon !== originalIcon) {
        queryParams.packageIcon = newIcon;
      }

      // Only include optional fields if they have values
      if (
        newParcelWeight.orderId !== null &&
        newParcelWeight.orderId !== undefined &&
        newParcelWeight.orderId !== ''
      ) {
        queryParams.order = newParcelWeight.orderId;
      }
      if (
        newParcelWeight.additionalCharge !== null &&
        newParcelWeight.additionalCharge !== undefined &&
        newParcelWeight.additionalCharge !== ''
      ) {
        queryParams.additionalCharge = newParcelWeight.additionalCharge;
      }

      parcelWeightService
        .updateParcelWeight(selectedParcelWeight.packageWeightId, queryParams)
        .then(() => {
          toast.success('Package weight updated successfully');
          fetchParcelWeights();
          handleModalClose();
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message;
          if (error.response?.data?.status === false) {
            toast.error(errorMessage);
          } else {
            toast.error(
              'Error editing parcel weight:',
              error.response?.data?.detail || error.message,
            );
          }
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setActionLoading(true);
    parcelWeightService
      .deleteParcelWeight(selectedParcelWeight.packageWeightId)
      .then(() => {
        toast.success(
          `Parcel weight ID ${selectedParcelWeight.packageWeightId} deleted successfully.`,
        );
        fetchParcelWeights();
        setDeleteDialogOpen(false);
        setSelectedParcelWeight(null);
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.message;
        toast.error(errorMessage);
      })
      .finally(() => setActionLoading(false));
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedParcelWeight(null);
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <span className="global_loader"></span>
      </div>
    );
  }

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Package Weights</h3>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                textTransform: 'none',
                borderColor: colors.swiftcarry[100],
                color: colors.swiftcarry[100],
                '&:hover': {
                  borderColor: colors.swiftcarry[200],
                  backgroundColor: 'transparent',
                },
              }}
            >
              Refresh
            </Button>
          </Box>
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={9} md={10}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by weight name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, color: 'gray' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ padding: '4px' }}
                        >
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleModalOpen(false)}
                  fullWidth
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                    },
                    height: '40px',
                  }}
                >
                  Add Weight
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package Weight ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Weight Name
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Weight Icon
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Order ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Additional Charge
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created On
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created By
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <TableRow key={row.packageWeightId}>
                          <TableCell>{row.packageWeightId ?? '—'}</TableCell>
                          <TableCell>{row.packageWeightName}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                bgcolor: colors.swiftcarry[100],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              }}
                            >
                              {row.packageWeightIcon || '📦'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {row.orderId ?? row.order ?? ''}
                          </TableCell>
                          <TableCell>
                            {row.additionalCharge != null && row.additionalCharge !== ''
                              ? row.additionalCharge
                              : '—'}
                          </TableCell>
                          <TableCell>{formatToIST(row.createdOn)}</TableCell>
                          <TableCell>{row.createdBy || 'Unknown'}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(event) => handleMenuOpen(event, row)}
                              disabled={actionLoading}
                            >
                              <Settings />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No parcel weights found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiToolbar-root': {
                  display: 'flex',
                  width: '500px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                '& .MuiTablePagination-selectLabel': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-displayedRows': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                },
                '& .MuiTablePagination-input': {
                  minWidth: '30px',
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleModalOpen(true);
                  handleMenuClose();
                }}
              >
                Update
              </DropdownItem>
              <DropdownItem onClick={handleDeleteClick}>Delete</DropdownItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmber
              sx={{
                fontSize: 28,
                color: 'error.main',
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontSize: '15px',
              lineHeight: 1.6,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Are you sure you want to delete the parcel weight{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              "{selectedParcelWeight?.packageWeightName}"
            </Box>
            ?
          </DialogContentText>
          <DialogContentText
            sx={{
              fontSize: '14px',
              color: 'error.main',
              fontWeight: 500,
              mt: 1,
            }}
          >
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              borderColor: 'text.secondary',
              color: 'text.secondary',
              minWidth: 100,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'text.primary',
                color: 'text.primary',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={actionLoading}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              bgcolor: 'error.main',
              minWidth: 100,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                boxShadow: 'none',
              },
            }}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            outline: 'none',
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            {isEditMode ? 'Edit Parcel Weight' : 'Add Parcel Weight'}
          </Typography>

          <TextField
            label="Weight Name"
            required={!isEditMode}
            variant="outlined"
            fullWidth
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                minHeight: '56px',
                transition: 'all 0.2s ease-in-out',
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px',
                },
                '&:hover:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.swiftcarry[100],
                  },
                },
                '&.Mui-focused:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: colors.swiftcarry[100],
                  },
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
                fontWeight: 600,
              },
            }}
            value={newParcelWeight.packageWeightName || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^A-Za-z0-9\s'-]/g, '');
              setNewParcelWeight({
                ...newParcelWeight,
                packageWeightName: value,
              });
              setErrors({ ...errors, packageWeightName: '' });
            }}
            error={!!errors.packageWeightName}
            helperText={errors.packageWeightName}
          />

          <TextField
            label="Package Icon"
            required={!isEditMode}
            variant="outlined"
            fullWidth
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                minHeight: '56px',
                transition: 'all 0.2s ease-in-out',
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px',
                },
                '&:hover:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.swiftcarry[100],
                  },
                },
                '&.Mui-focused:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: colors.swiftcarry[100],
                  },
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
                fontWeight: 600,
              },
            }}
            value={newParcelWeight.packageIcon || ''}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^A-Za-z\s'-]/g, '')
                .slice(0, 5);
              setNewParcelWeight({
                ...newParcelWeight,
                packageIcon: value,
              });
              setErrors({ ...errors, packageIcon: '' });
            }}
            error={!!errors.packageIcon}
            helperText={errors.packageIcon}
            inputProps={{ maxLength: 5 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.isHeavyVehicle}>
            <InputLabel
              sx={{
                fontWeight: 500,
                '&.Mui-focused': {
                  color: errors.isHeavyVehicle
                    ? 'error.main'
                    : colors.swiftcarry[100],
                  fontWeight: 600,
                },
              }}
            >
              Heavy Vehicle
            </InputLabel>
            <Select
              value={
                newParcelWeight.isHeavyVehicle != null
                  ? newParcelWeight.isHeavyVehicle
                  : ''
              }
              label="Heavy Vehicle"
              displayEmpty={isEditMode}
              onChange={(e) => {
                setNewParcelWeight({
                  ...newParcelWeight,
                  isHeavyVehicle: e.target.value === '' ? '' : e.target.value,
                });
                setErrors({ ...errors, isHeavyVehicle: '' });
              }}
              sx={{
                borderRadius: 2,
                backgroundColor: 'background.paper',
                minHeight: '56px',
                transition: 'all 0.2s ease-in-out',
                '&:hover:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.swiftcarry[100],
                  },
                },
                '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                  {
                    borderWidth: 2,
                    borderColor: colors.swiftcarry[100],
                  },
              }}
            >
              {isEditMode && (
                <DropdownItem value="">
                  <em>None</em>
                </DropdownItem>
              )}
              <DropdownItem value={true}>True</DropdownItem>
              <DropdownItem value={false}>False</DropdownItem>
            </Select>
            {errors.isHeavyVehicle && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: 'block', fontWeight: 500 }}
              >
                {errors.isHeavyVehicle}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Order ID"
            required={!isEditMode}
            type="number"
            variant="outlined"
            fullWidth
            inputProps={{
              min: 0,
              step: 1,
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                minHeight: '56px',
                transition: 'all 0.2s ease-in-out',
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px',
                },
                '&:hover:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.swiftcarry[100],
                  },
                },
                '&.Mui-focused:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: colors.swiftcarry[100],
                  },
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
                fontWeight: 600,
              },
            }}
            value={
              newParcelWeight.orderId !== null &&
              newParcelWeight.orderId !== undefined &&
              newParcelWeight.orderId !== ''
                ? newParcelWeight.orderId
                : ''
            }
            onChange={(e) => {
              const value = e.target.value === '' ? '' : e.target.value;
              setNewParcelWeight({
                ...newParcelWeight,
                orderId: value,
              });
              setErrors({ ...errors, orderId: '' });
            }}
            error={!!errors.orderId}
            helperText={errors.orderId}
          />

          <TextField
            label="Additional Charge"
            required={!isEditMode}
            type="number"
            variant="outlined"
            fullWidth
            inputProps={{
              min: 0,
              step: 1,
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                minHeight: '56px',
                transition: 'all 0.2s ease-in-out',
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px',
                },
                '&:hover:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.swiftcarry[100],
                  },
                },
                '&.Mui-focused:not(.Mui-error)': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: colors.swiftcarry[100],
                  },
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.swiftcarry[100],
                fontWeight: 600,
              },
            }}
            value={
              newParcelWeight.additionalCharge !== null &&
              newParcelWeight.additionalCharge !== undefined
                ? newParcelWeight.additionalCharge
                : ''
            }
            onChange={(e) => {
              const value = e.target.value === '' ? '' : e.target.value;
              setNewParcelWeight({
                ...newParcelWeight,
                additionalCharge: value,
              });
              setErrors({ ...errors, additionalCharge: '' });
            }}
            error={!!errors.additionalCharge}
            helperText={errors.additionalCharge}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              disabled={actionLoading}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 500,
                borderColor: 'text.secondary',
                color: 'text.secondary',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'text.primary',
                  color: 'text.primary',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={
                isEditMode ? handleEditParcelWeight : handleAddParcelWeight
              }
              disabled={
                actionLoading ||
                (!isEditMode &&
                  (!newParcelWeight.packageWeightName ||
                    !newParcelWeight.packageIcon ||
                    newParcelWeight.isHeavyVehicle === null ||
                    newParcelWeight.isHeavyVehicle === undefined ||
                    newParcelWeight.isHeavyVehicle === '' ||
                    !newParcelWeight.orderId ||
                    newParcelWeight.orderId.toString().trim() === '' ||
                    !newParcelWeight.additionalCharge ||
                    newParcelWeight.additionalCharge.toString().trim() === ''))
              }
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                bgcolor: colors.swiftcarry[100],
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: colors.swiftcarry[200],
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  boxShadow: 'none',
                },
              }}
            >
              {actionLoading
                ? 'Processing...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Add Parcel Weight'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ParcelWeights;
